import React, { useEffect, useRef, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useConvexAuth } from 'convex/react';

import { RootState } from '../../store';
import { clearInviteRequired } from '../../store/trail';

/**
 * Full-screen gate shown when a scanned/entered code belongs to an invite-only
 * trail the player can't access yet. Mounted once at the router root (only
 * when playerAuthEnabled, so the ConvexBetterAuthProvider is guaranteed to be
 * present); renders nothing until `trail.inviteRequired` is set.
 */
const InviteGate: React.FC = (): JSX.Element | null => {
  const inviteRequired = useSelector(
    (state: RootState) => state.trail.inviteRequired
  );

  if (!inviteRequired) return null;
  return <InviteGateScreen />;
};

const InviteGateScreen: React.FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading } = useConvexAuth();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Modal semantics: move keyboard focus into the gate when it appears.
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-gate-title"
      aria-describedby="invite-gate-copy"
      tabIndex={-1}
      className="start-screen fixed inset-0 z-[1000] flex h-screen flex-col items-center justify-center gap-6 bg-cover bg-center bg-no-repeat outline-none"
    >
      <div className="bg-mt-surface flex w-72 flex-col gap-4 rounded-2xl p-6 text-center shadow-2xl">
        <h1
          id="invite-gate-title"
          className="text-mt-ink-soft text-xl font-bold tracking-tight"
        >
          This trail is invite-only
        </h1>
        {/* Don't claim the player is signed out while auth is still
            initialising — hold neutral copy until it settles. */}
        {isLoading ? (
          <p id="invite-gate-copy" className="text-mt-ink-muted text-sm">
            Checking your account…
          </p>
        ) : isAuthenticated ? (
          <p id="invite-gate-copy" className="text-mt-ink-muted text-sm">
            You're signed in, but you don't have access to this trail yet. Ask
            whoever invited you for an invite link.
          </p>
        ) : (
          <p id="invite-gate-copy" className="text-mt-ink-muted text-sm">
            You need an account to play this trail. Sign in, then open your
            invite link to claim it.
          </p>
        )}

        {!isLoading && !isAuthenticated && (
          <Link
            to="/account"
            onClick={() => dispatch(clearInviteRequired())}
            className="mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover inline-flex items-center justify-center gap-x-2 rounded-md px-3.5 py-2.5 text-sm font-semibold shadow-xs"
          >
            Sign in
          </Link>
        )}

        <p className="text-mt-ink-faint text-xs">
          Have an invite link? Open it to get access.
        </p>

        <Link
          to="/"
          onClick={() => dispatch(clearInviteRequired())}
          className="text-mt-primary text-sm hover:underline"
        >
          Back home
        </Link>
      </div>
    </div>
  );
};

export default InviteGate;
